<script setup>
import {computed, inject, reactive} from "vue";
//import functions
import {required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useI18n} from "vue-i18n";
import {useVuelidate} from "@vuelidate/core";

//stores
const userStore = useUserStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  _selectedUser: Object
});

//injects
const visible = inject('isShowAddUserModal')

//component variables
const {t, locale} = useI18n({useScope: 'global'})
const user = reactive({
  "name": '',
  "username": '',
  "password": ''
})
const rules = computed(() => {
  return {
    name: {required},
    username: {required},
    password: {required},
  };
});
const $vuelidate = useVuelidate(rules, user);

//global functions

//component functions
function reset() {
  //form reset
  $vuelidate.value.$reset()
}

const addUser = async () => {
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  setSaving(true)
  try {
    const res = await userStore.action_addUser(user)
    if (res.result === true) {
      //toast.add({severity: 'success', summary: 'Successful', detail: 'User Added', life: 500});
      showMessage(`User Added!`, 'success');
      visible.value = false
      //await userStore.action_getAllUsers()
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    setSaving(false)
  }
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible" @hide="reset"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Add User
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full">
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
        <FloatLabel variant="on">
          <InputText id="name" v-model="user.name" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     :class="{ 'has-error': $vuelidate.name.$error }"/>
          <label for="name" class="!bg-slate-900"
                 :class="{ 'focus:text-red-300': $vuelidate.name.$error }">Name</label>
        </FloatLabel>
        <template v-if="$vuelidate.name.$error">
          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
             v-if="!$vuelidate.name.required.$response">
            This field is required
          </p>
        </template>
      </div>
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
        <FloatLabel variant="on">
          <InputText id="username" v-model="user.username" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     :class="{ 'has-error': $vuelidate.username.$error }"/>
          <label for="username" class="!bg-slate-900"
                 :class="{ 'focus:text-red-300': $vuelidate.username.$error }">Username</label>
        </FloatLabel>
        <template v-if="$vuelidate.username.$error">
          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
             v-if="!$vuelidate.username.required.$response">
            This field is required
          </p>
        </template>
      </div>
      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
        <FloatLabel variant="on">
          <InputText id="password" v-model="user.password" autocomplete="off"
                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                     :class="{ 'has-error': $vuelidate.password.$error }"/>
          <label for="password" class="!bg-slate-900"
                 :class="{ 'focus:text-red-300': $vuelidate.password.$error }">Password</label>
        </FloatLabel>
        <template v-if="$vuelidate.password.$error">
          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
             v-if="!$vuelidate.password.required.$response">
            This field is required
          </p>
        </template>
      </div>
    </div>

    <div class="flex justify-end gap-3">
      <button @click="addUser"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style></style>